import { Spinner_v2 as Spinner, Typography, TypographyVariantsEnum } from '@mindly/ui-components';
import { FormattedMessage } from 'react-intl';
import { ApplePayIcon, GooglePayIcon, useLog, useStripePayments } from 'src/shared';
import { isPlatform, useIonViewDidEnter } from '@ionic/react';
import { useCallback, useEffect, useState } from 'react';
import { ApplePayEventsEnum, GooglePayEventsEnum, Stripe } from '@capacitor-community/stripe';
import { SupportedCurrency } from '@mindly/ui-components';
import { useStripe, useElements } from '@stripe/react-stripe-js';
import useToast from 'src/context/ToastContext/ToastContext';
import { ExpressCheckout } from 'src/shared/ui/ExpressCheckout';
import { environment } from 'src/enviroments';
import { PrivateRoutes } from 'src/routes/navigation';

interface PaymentProvidersFeatureProps {
  stripeChargeCurrency: SupportedCurrency;
  stripeChargeAmount: number;
  label: string;
  disabled?: boolean;
  onSuccess: () => void;
  onClick?: (type: 'apple' | 'google') => void;
  confirmPayment: () => Promise<{ amount: number; clientSecret: string }>;
  usePaymentRequest?: boolean;
}

const PaymentProvidersFeature: React.FC<PaymentProvidersFeatureProps> = ({
  disabled,
  onSuccess,
  onClick,
  stripeChargeCurrency,
  stripeChargeAmount,
  label,
  confirmPayment,
  usePaymentRequest = false,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const { showToast } = useToast();
  const [isProviderButtonLoading, setIsProviderButtonLoading] = useState(false);

  const [isExpressCheckoutLoading, setIsExpressCheckoutLoading] = useState(true);
  const [isPaymentRequestLoading, setIsPaymentRequestLoading] = useState(false);

  const [isExpressCheckoutError, setIsExpressCheckoutError] = useState<boolean>(false);
  const [webApplePayAvailable, setWebApplePayAvailable] = useState(false);
  const [webGooglePayAvailable, setWebGooglePayAvailable] = useState(false);
  const { logError } = useLog();

  const {
    countryCode,
    isGooglePayAvailable,
    isApplePayAvailable,
    initiateGooglePayTransaction,
    initiateApplePayTransaction,
  } = useStripePayments();

  // Check web-based payment availability
  const checkWebPaymentAvailability = useCallback(async () => {
    if (!stripe || !usePaymentRequest || isPlatform('hybrid')) {
      return;
    }

    try {
      setIsPaymentRequestLoading(true);
      const applePayRequest = stripe.paymentRequest({
        country: countryCode,
        currency: stripeChargeCurrency.toLowerCase(),
        total: {
          label,
          amount: stripeChargeAmount,
        },
        disableWallets: ['link', 'browserCard'],
        requestPayerName: true,
      });

      const googlePayRequest = stripe.paymentRequest({
        country: countryCode,
        currency: stripeChargeCurrency.toLowerCase(),
        total: {
          label,
          amount: stripeChargeAmount,
        },
        disableWallets: ['link', 'browserCard'],
        requestPayerName: true,
      });

      const [applePayCanMakePayment, googlePayCanMakePayment] = await Promise.all([
        applePayRequest.canMakePayment(),
        googlePayRequest.canMakePayment(),
      ]);

      setWebApplePayAvailable(!!applePayCanMakePayment?.applePay);
      setWebGooglePayAvailable(!!googlePayCanMakePayment?.googlePay);
    } catch (error) {
      console.error('Error checking web payment availability:', error);
      logError(error);
    } finally {
      setIsPaymentRequestLoading(false);
    }
  }, [stripe, usePaymentRequest, countryCode, stripeChargeCurrency, stripeChargeAmount, label]);

  useEffect(() => {
    checkWebPaymentAvailability();
  }, [checkWebPaymentAvailability]);

  const handleNativeAppleOrGooglePay = useCallback(
    (type: 'apple' | 'google') => async () => {
      try {
        onClick?.(type);
        setIsProviderButtonLoading(true);
        const initiateAppleOrGooglePayTransaction =
          type === 'apple' ? initiateApplePayTransaction : initiateGooglePayTransaction;

        const { clientSecret, amount } = await confirmPayment();

        const result = await initiateAppleOrGooglePayTransaction({
          paymentIntentClientSecret: clientSecret,
          paymentSummaryItems: [
            {
              label,
              amount: amount / 100,
            },
          ],
          paymentIntentCurrency: stripeChargeCurrency,
        });

        if (result === ApplePayEventsEnum.Completed || result === GooglePayEventsEnum.Completed) {
          onSuccess();
        } else if (
          [ApplePayEventsEnum.Failed, GooglePayEventsEnum.Failed].includes(
            result as ApplePayEventsEnum | GooglePayEventsEnum
          )
        ) {
          throw new Error('generic_decline');
        }
      } catch (error: any) {
        console.error('PaymentProvider: Native payment error:', error);
        showToast({ error });
      } finally {
        setIsProviderButtonLoading(false);
      }
    },
    [
      stripeChargeCurrency,
      stripeChargeAmount,
      confirmPayment,
      initiateApplePayTransaction,
      initiateGooglePayTransaction,
      onSuccess,
      showToast,
    ]
  );

  const handleReceivePaymentMethodFromWebProvider = useCallback(
    async (ev: any) => {
      try {
        if (!stripe) {
          return;
        }

        const { clientSecret } = await confirmPayment();
        const { paymentIntent, error: confirmError } = await stripe.confirmCardPayment(
          clientSecret,
          { payment_method: ev.paymentMethod.id },
          { handleActions: false }
        );

        if (confirmError) {
          ev.complete('fail');
          return showToast({ error: confirmError });
        }

        ev.complete('success');

        if (paymentIntent.status === 'requires_action') {
          const { error } = await stripe.confirmCardPayment(clientSecret);
          if (error) {
            ev.complete('fail');
            return showToast({ error });
          }
        }

        onSuccess();
      } catch (error) {
        ev.complete('fail');
        showToast({ error });
      }
    },
    [stripe, onSuccess, confirmPayment, showToast]
  );

  const handleWebAppleOrGooglePay = useCallback(
    (type: 'apple' | 'google') => async () => {
      try {
        onClick?.(type);
        setIsProviderButtonLoading(true);
        if (!stripe) {
          return;
        }

        const localPaymentRequest = stripe.paymentRequest({
          country: countryCode,
          currency: stripeChargeCurrency.toLowerCase(),
          total: {
            label,
            amount: stripeChargeAmount,
          },
          disableWallets: ['link', 'browserCard'],
          requestPayerName: true,
        });

        localPaymentRequest.on('paymentmethod', handleReceivePaymentMethodFromWebProvider);
        const canMakePaymentResult = await localPaymentRequest.canMakePayment();

        if (canMakePaymentResult) {
          localPaymentRequest.show();
        }
      } catch (error: any) {
        showToast({ error });
      } finally {
        setIsProviderButtonLoading(false);
      }
    },
    [stripe, stripeChargeAmount, stripeChargeCurrency, countryCode, label, confirmPayment, showToast]
  );

  useIonViewDidEnter(() => {
    Stripe.addListener(ApplePayEventsEnum.Failed, (error) => {
      showToast({ message: (error as unknown as { error: string })?.error });
    });
  }, []);

  const confirmExpressCheckoutPayment = useCallback(async () => {
    if (!stripe || !elements) {
      logError(new Error('PaymentProvidersFeature: Elements or stripe not found, stripe loading error'));
      return;
    }

    const { clientSecret } = await confirmPayment();
    const { error } = await stripe.confirmPayment({
      elements: elements,
      clientSecret: clientSecret,
      redirect: 'if_required',
      confirmParams: {
        return_url: `${environment.MAIN_WEB_URL}${PrivateRoutes.CONSULTATION}`,
      },
    });

    if (error) {
      throw new Error(error.message);
    }
  }, [stripe, elements, confirmPayment]);

  return (
    <>
      {!isPlatform('hybrid') && !usePaymentRequest && (
        <>
          <div className={isExpressCheckoutLoading ? 'mb-5' : ''}>
            <ExpressCheckout
              onReady={() => setIsExpressCheckoutLoading(false)}
              confirmExpressCheckout={confirmExpressCheckoutPayment}
              onSuccess={onSuccess}
              onLoadError={() => setIsExpressCheckoutError(true)}
              showError={false}
            />
          </div>

          {!isExpressCheckoutError && !isExpressCheckoutLoading && (
            <Typography variant={TypographyVariantsEnum.Text14Regular} className={'text-center my-5'}>
              <FormattedMessage id={'pay.provider.pay'} defaultMessage={'Або'} />
            </Typography>
          )}
        </>
      )}

      {isPaymentRequestLoading && usePaymentRequest && <Spinner className="mx-auto mb-5" />}
      {(isApplePayAvailable || webApplePayAvailable) && (
        <>
          <button
            className={
              'w-full rounded-lg border-0 flex h-12 p-2 justify-center items-center space-x-1 mb-5 bg-color-black'
            }
            onClick={isPlatform('hybrid') ? handleNativeAppleOrGooglePay('apple') : handleWebAppleOrGooglePay('apple')}
            disabled={disabled}
          >
            {isProviderButtonLoading ? (
              <Spinner size={24} className={'text-color-white'} />
            ) : (
              <Typography className={'!text-color-white'} variant={TypographyVariantsEnum.Text20Semi}>
                <ApplePayIcon />
              </Typography>
            )}
          </button>
          <Typography variant={TypographyVariantsEnum.Text14Regular} className={'text-center mb-5'}>
            <FormattedMessage id={'pay.provider.pay'} defaultMessage={'Або'} />
          </Typography>
        </>
      )}

      {(isGooglePayAvailable || webGooglePayAvailable) && (
        <>
          <button
            className={
              'w-full rounded-lg border-0 flex h-12 p-2 justify-center items-center space-x-1 mb-5 bg-color-black'
            }
            onClick={
              isPlatform('hybrid') ? handleNativeAppleOrGooglePay('google') : handleWebAppleOrGooglePay('google')
            }
            disabled={disabled}
          >
            {isProviderButtonLoading ? (
              <Spinner size={24} className={'text-color-white'} />
            ) : (
              <Typography
                className={'!text-color-white flex items-center gap-2'}
                variant={TypographyVariantsEnum.Text20Semi}
              >
                <GooglePayIcon />
              </Typography>
            )}
          </button>
          <Typography variant={TypographyVariantsEnum.Text14Regular} className={'text-center mb-5'}>
            <FormattedMessage id={'pay.provider.pay'} defaultMessage={'Або'} />
          </Typography>
        </>
      )}
    </>
  );
};

export default PaymentProvidersFeature;
